import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { isDemo } from '../account/beta.ts';
import { useAccount } from '../account/store.ts';
import { api } from '../api/instance.ts';
import { claimTrialFlow } from '../app/accountActions.ts';
import { toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { checkoutPlan } from './billing.ts';
import { planName, trialEligible } from './features.ts';
import { usePlans } from './store.ts';
import { Turnstile } from './Turnstile.tsx';

/** Trial paths the server enables for web (`trialPaths`: "nocard" = POST /v1/trial, "stripe" = checkout trial). */
export function trialPaths(): string[] {
  const me = useAccount.getState().me;
  return me?.trialPaths ?? (me?.trialAvailable ? ['nocard'] : []);
}

/** "Start free trial" (no-card via Turnstile, or a Stripe card trial). Renders nothing when unavailable. */
export function TrialOffer({ onDone }: { onDone?: () => void }) {
  const { me } = useAccount(useShallow((s) => ({ me: s.me })));
  const catalog = usePlans((s) => s.catalog);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me?.trialAvailable || !trialEligible(me)) return null;
  const paths = trialPaths();
  const trial = catalog?.trial;
  const label = trial ? `Try ${planName(trial.plan, catalog)} free for ${trial.days} days` : 'Start free trial';
  const credits = trial ? ` · ${trial.aiCredits} AI credits` : '';

  if (paths.includes('nocard')) {
    if (challenge) {
      return (
        <div className="flex flex-col gap-2">
          <span className="t-caption text-dim">Quick check that you’re human…</span>
          <Turnstile
            challenge={challenge}
            onToken={async (token) => {
              if (await claimTrialFlow({ challenge, turnstileToken: token })) onDone?.();
              setChallenge(null);
            }}
            onError={(m) => {
              toast(m, 'error');
              setChallenge(null);
            }}
          />
        </div>
      );
    }
    return (
      <Button
        variant="primary"
        full
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            setChallenge((await api.attestChallenge()).challenge);
          } catch {
            // Server without attestation: try the claim directly.
            if (await claimTrialFlow()) onDone?.();
          } finally {
            setBusy(false);
          }
        }}
      >
        {label}
        {credits}
      </Button>
    );
  }
  if (paths.includes('stripe') && trial && !isDemo(me)) {
    return (
      <Button variant="primary" full onClick={() => void checkoutPlan(trial.plan, 'month')}>
        {label}
      </Button>
    );
  }
  return null;
}
